import { isLeader } from 'entities';
import { createRoutes } from './build';

import { StringObj } from 'types';

export const routes = createRoutes({
  login: {
    path: '/login',
    isOpen: true,

    title: 'Login',
  },

  validatePDF: {
    path: '/project-document-validity',
    isOpen: true,

    title: 'Validate Project PDF',
  },

  home: {
    path: '/',

    title: 'Home',
  },

  issuesHome: {
    path: '/issues',

    title: 'Project Issues',
  },

  recruitmentHome: {
    path: '/recruitment',

    permissions: {
      team: {
        isRecruitmentTeam: true,
      },
    },

    title: 'Recruitment Home',
  },

  invoiceHome: {
    path: '/invoice',

    permissions: {
      team: {
        isInvoiceTeam: true,
      },
    },

    title: 'Invoice Home',

    children: {
      invoices: {
        path: '/invoices',

        permissions: {
          team: {
            isInvoiceTeam: true,
          },
        },

        title: 'Invoices',

        queryParams: ['status', 'invoice'] as const,
      },

      outsourceInvoices: {
        path: '/outsource-invoices',

        title: 'Outsource Invoices',

        permissions: {
          team: {
            isInvoiceTeam: true,
          },
        },

        queryParams: ['status'] as const,
      },

      invoiceProjectsByStatus: {
        path: 'projects/:status',
        getPath: ({ status }: StringObj<'status'>) => `/projects/${status}`,

        permissions: {
          team: {
            isInvoiceTeam: true,
          },
        },

        title: 'Invoice Projects By Status',
      },

      clientsBalance: {
        path: '/clients/balance',
        title: 'Client Balances',

        customPermissionCheck: ({ isSysAdmin, team }) => {
          if (isSysAdmin) return true;

          return !!team.isInvoiceTeam;
        },
      },

      packages: {
        path: '/packages',

        permissions: {
          team: {
            isInvoiceTeam: true,
          },
        },

        title: 'Invoice Packages',

        children: {
          createPackage: {
            path: '/new',

            permissions: {
              team: {
                isInvoiceTeam: true,
              },
              type: 'LEADER',
            },

            title: 'Create Invoice Package',
          },

          packageProfile: {
            path: '/package/:packageId',
            getPath: ({ packageId }: StringObj<'packageId'>) => `/package/${packageId}`,

            permissions: {
              team: {
                isInvoiceTeam: true,
              },
            },

            title: 'Invoice Package',
          },
        },
      },
    },
  },

  profile: {
    path: '/profile',

    title: 'My Profile',
  },

  notifications: {
    path: '/notifications',

    title: 'Notifications',
  },

  teams: {
    path: '/teams',
    title: 'Teams',

    permissions: {
      isSysAdmin: true,
    },

    children: {
      createTeam: {
        path: '/teams/create',
        title: 'Create Team',

        permissions: {
          isSysAdmin: true,
        },
      },

      teamProfile: {
        title: 'Edit Team',

        path: '/teams/:teamId',
        getPath: ({ teamId }: StringObj<'teamId'>) => `/teams/${teamId}`,
      },
    },
  },

  offices: {
    path: '/offices',
    title: 'Offices',

    permissions: {
      isSysAdmin: true,
    },
  },

  outsourceProviders: {
    path: '/outsource-providers',
    title: 'Outsource Providers',

    permissions: {
      isSysAdmin: true,
    },
  },

  clients: {
    path: '/clients',
    title: 'Clients',

    customPermissionCheck: ({ isSysAdmin, team, type }) => {
      if (isSysAdmin) return true;

      return !!team.clientsPageAccess && isLeader({ type });
    },
  },

  createProject: {
    path: '/projects/create',
    title: 'Create Project',

    permissions: {
      team: {
        canCreateProject: true,
      },
    },
  },

  tasksOverview: {
    path: '/projects/tasks',
    title: 'Task Overview',
    queryParams: ['tab'] as const,
  },

  projectProfile: {
    title: 'Project Profile',
    path: '/projects/:projectId',
    getPath: ({ projectId }: StringObj<'projectId'>) => `/projects/${projectId}`,
    queryParams: ['tab', 'issueId', 'taskId'] as const,
  },

  pendingActions: {
    path: '/pending-actions',

    title: 'Pending Actions',

    queryParams: ['tab'] as const,
  },

  mySuggestions: {
    path: '/feedback/my-suggestions',

    title: 'My Suggestions',
  },

  userSuggestions: {
    path: '/feedback/suggestions',

    title: 'User Suggestions',

    queryParams: ['suggestionId'] as const,
  },

  bugReports: {
    path: '/feedback/bug-reports',

    title: 'Bug Reports',

    queryParams: ['reportId'] as const,

    permissions: {
      isSysAdmin: true,
    },
  },
});

export type RoutesType = typeof routes;

export type AppRoute = keyof RoutesType;
